import { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { getPaymentHistoryAction } from './API/Action';
import CustomChip from '../Components/CustomChip';
import StatusBadge from '../../v2/MUI_custom_component/Badge/StatusBadge';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';

const PAGE_SIZE = 10;

const statusFilters = [
  { value: 1, label: 'PENDING' },
  { value: 3, label: 'COMPLETED' },
  { value: 2, label: 'FAILED' },
];

export default function PaymentHistory({ refreshKey }) {
  const {
    selectedEntity: selectedCompanyEntity,
    selectedSupplier: selectedManufacturer,
  } = useSelector((state) => state.entityReducer);

  const [statusFilter, setStatusFilter] = useState(null);

  const fetchPaymentHistory = useCallback(
    async (page, pageSize) => {
      const config = new URLSearchParams();
      config.append('f', 'id$gte$0');
      config.append('o', 'id$desc');
      config.append('page', page.toString());
      config.append('page_size', pageSize.toString());
      config.append('seller_id', selectedManufacturer);
      config.append('buyer_esi_id', selectedCompanyEntity);
      if (statusFilter !== null) {
        const statusLabel = statusFilters.find(
          (s) => s.value === statusFilter,
        )?.label;
        if (statusLabel) config.append('f', `status$eq$${statusLabel}`);
      }

      try {
        const result = await getPaymentHistoryAction(config);
        if (result.error) {
          toast.error(result.message || 'Failed to fetch payment history');
          return { error: true, data: [] };
        }
        return { data: result?.data || [] };
      } catch {
        toast.error('Failed to fetch payment history');
        return { error: true, data: [] };
      }
    },
    [selectedCompanyEntity, selectedManufacturer, statusFilter],
  );

  const { data: paymentList, loading } = useInfiniteScroll({
    fetchFunction: fetchPaymentHistory,
    pageSize: PAGE_SIZE,
    dependencies:
      selectedCompanyEntity && selectedManufacturer
        ? [
            selectedCompanyEntity,
            selectedManufacturer,
            statusFilter,
            refreshKey,
          ]
        : [],
  });
  if (loading && paymentList?.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && paymentList?.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Typography variant='h2'>Payment History</Typography>

      {/* Filter chips */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          pt: { xs: 0, md: 2 },
          overflowX: 'scroll',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {statusFilters
          .map((filter) => ({
            ...filter,
            selected: statusFilter === filter.value,
          }))
          .sort((a, b) => b.selected - a.selected)
          .map((filter) => (
            <CustomChip
              key={filter.value}
              label={
                filter.label.charAt(0) + filter.label.slice(1).toLowerCase()
              }
              selected={filter.selected}
                variant={filter.selected ? 'filled' : 'outlined'}

              onClick={() => setStatusFilter(filter.value)}
              onDelete={
                filter.selected ? () => setStatusFilter(null) : undefined
              }
            />
          ))}
      </Box>

      {/* Table */}
      <TableContainer
        variant='outlined'
        sx={{
          border: 'none',
          boxShadow: 'none',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          overflowX: 'scroll',
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: '#E5E5E5',
                '& .MuiTableCell-root': {
                  borderRight: 'none',
                  borderBottom: '1px solid #8C8C8C',
                },
              }}
            >
              <TableCell sx={{ py: 2, px: 1 }}>
                <Typography
                  variant='labelM'
                  sx={{ minWidth: { xs: '50px', md: '80px' } }}
                >
                  Payment Date
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 2, px: 1 }}>
                <Typography
                  variant='labelM'
                  sx={{ minWidth: { xs: '50px', md: '120px' } }}
                >
                  Transaction ID
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 2, px: 1 }}>
                <Typography
                  variant='labelM'
                  sx={{ minWidth: { xs: '60px', md: '120px' } }}
                >
                  Amount
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 2, px: 1 }}>
                <Typography
                  variant='labelM'
                  sx={{ minWidth: { xs: '50px', md: '100px' } }}
                >
                  Status
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentList?.map((row, index) => (
              <TableRow
                key={row.id ?? index}
                sx={{
                  '& .MuiTableCell-root': {
                    borderRight: 'none',
                    py: 1,
                    px: 1,
                  },
                }}
              >
                <TableCell>
                  <Typography variant='p3'>
                    {row?.date ? dayjs(row?.date).format('DD/MM/YYYY') : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='p3'>
                    {row?.payment_gateway_transaction_id || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='p3'>₹ {row?.amount ?? '-'}</Typography>
                </TableCell>
                <TableCell>
                  <StatusBadge
                    label={
                      statusFilters.find((s) => s.value === row?.status)
                        ?.label ?? String(row?.status)
                    }
                    status={
                      statusFilters.find((s) => s.value === row?.status)
                        ?.label ?? String(row?.status)
                    }
                    size='medium'
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
